import React from "react";
import { Link } from "react-router-dom";
import "../assets/styles/legal-pages.css";
import JoinCommunityAndFooter from "../components/landing/JoinCommunityAndFooter";

/** Privacy Policy page — styles in `legal-pages.css`. */
const PrivacyPolicy = (): JSX.Element => {
	return (
		<>
			<div className="legal-doc-page">
				<header className="legal-doc-header">
					<div className="legal-doc-header-inner">
						<Link className="legal-doc-home" to="/" aria-label="Back to home" title="Home">
							<svg viewBox="0 0 24 24" aria-hidden>
								<path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill="currentColor" />
							</svg>
						</Link>
						<span className="legal-doc-site-title">Privacy policy</span>
						<span className="legal-doc-header-spacer" aria-hidden />
					</div>
				</header>
				<main className="legal-doc-main legal-doc-prose">
<h1>DFIANCE PRIVACY POLICY</h1>
<p>Last Updated: July 2025</p>
<br/>
<h2>INTRODUCTION</h2>
<br/>
<h3>1. Purpose</h3>
<p>
Welcome to DFIANCE, an interactive digital trading card game (“DFIANCE”), operated by Voidspark Interactive Ltd, a company registered in the British Virgin Islands under registration number 2171833, with its registered office at Craigmuir Chambers, Road Town, Tortola, VG 1110. ("Voidspark", "Company", "we", "us", "our"). We are committed to ensuring the privacy, security, and protection of all users interacting with our gaming platform, the Dfiance.com game, our website, and other related services.
</p>
<p>
This Privacy Policy outlines how we collect, use, disclose, store, and protect personal data. It also explains your rights and choices regarding your personal information and how we comply with applicable privacy laws and industry standards.
</p>
<h3>2. Scope</h3>
<p>This Privacy Policy applies to:</p>
<ul>
<li>
All occasional (guest) users and/or registered users, users who play the DFIANCE interactive digital card game on any of the supported platforms, users that make purchases or participate in any in-game DFIANCE interactions.
</li>
<li>
Visitors who browse DFIANCE and interact with support services, promotional events, and social features and channels.
</li>
<li>
Data collected from third-party integrations, gaming consoles, and linked accounts(e.g., Google, Discord, Telegram, Twitch, Steam, et cetera).
</li>
<li>
Data shared with our partners, affiliates, and service providers for operational and security purposes.
</li>
<li>
This policy does not cover third-party websites, applications, or services that may belinked from our platform. Users should review the privacy policies of third-party services separately.
</li>
</ul>

<h3>3. Acceptance</h3>
<p>
By accessing or using DFIANCE, you confirm that you have read, understood, and agreed to this Privacy Policy.
</p>
<ul>
<li>
If you do not agree, you must immediately discontinue use of our services.
</li>
<li>
If you are a parent or guardian providing consent for a minor, you acknowledge responsibilityfor the child’s use of our platform.
</li>
<li>
We may update this policy periodically to reflect changes in laws, regulations, security practices, and technology. Users are encouraged to review this document regularly.
</li>
</ul>
<br/>
<h2>INFORMATION WE COLLECT</h2>
<p>
We collect different types of data to provide a seamless, secure, and personalized gaming experience. This includes information you provide directly, automatically collected data, and information received from third parties.
</p>
<h3>4. Personal Data You Provide</h3>
<br/>
<p>When you create an account, interact with customer support, or participate in our community, we may collect any or all the following:</p>
<ul>
<li>
Email Address – Required for account creation, verification, password recovery, and communication.</li>
<li>
Username (e.g., Discord, Telegram, Steam, Twitch) – Used for profile identification, social features,and chat interactions.
</li>
<li>
Phone Number (if provided) – Used for two-factor authentication (2FA), identity verification, and support services.
</li>
<li>
Date of Birth (if required) – Used for age verification, legal compliance, and parental control settings.
</li>
<li>
Payment and Transaction Data – If purchases are made, we collect billing details, transaction history, and payment preferences (note: we do not store credit card details—transactions are processed through secure third-party payment providers).
</li>
<li>
User-Generated Content (UGC) – Includes forum posts, chat messages, gameplay interactions, support requests, and uploaded media.
</li>
<li>
Survey and Feedback Data – Information voluntarily provided in user feedback forms, research studies, and beta-testing programs.
</li>
</ul>
<h3>5. Automatically Collected Data</h3>
<p>
We automatically collect certain data when you use DFIANCE, including:
</p>
<ul>
<li>
IP Address – Used for security, fraud prevention, regional content adjustments, and performance analytics.
</li>
<li>
Device and Browser Information – Includes device type, operating system, browser version, screen resolution, hardware specs.
</li>
<li>
Usage Data – Logs pages visited, session duration, actions taken in-game, clicks, scroll behavior, and engagement with features.
</li>
<li>
Geolocation Data – If enabled, collected for regional matchmaking, security monitoring, and personalized game recommendations.
</li>
<li>
Crash Reports and Debug Data – Collected in case of game crashes, software bugs, and performance failures to improve stability.
</li>
</ul>
<h3>6. Data from Third-Party Services</h3>
<p>
If you link external accounts to your DFIANCE profile (e.g., Discord, Steam, Twitch, Google, Facebook), we may also collect:
</p>
<ul>
<li>
Account identifier & profile details – Including username, profile picture, friend lists (if permitted), in-game stats, DFIANCE access tokens, email and/or username, and linked accounts.
</li>
<li>
Transaction data – Purchases or in-game activity related to DFIANCE through third-party marketplaces.
</li>
<li>
Social Features Interactions – If you use voice chat, text chat, or participate in online forums, this data may be recorded or moderated.
</li>
</ul>
<h3>7. Cookies & Tracking Technologies</h3>
<p>
DFIANCE uses cookies, tracking pixels, and analytics tools to:
</p>
<ul>
<li>
Optimize site performance and reduce loading times.
</li>
<li>
Enable security features like fraud detection and account recovery.
</li>
<li>
Analyze user behavior to improve game design, matchmaking, and monetization strategies.
</li>
</ul>
<p>
Users can disable cookies via their browser settings, but some features may be limited as a result.
</p>
<br/>
<h2>LEGAL BASIS FOR PROCESSING PERSONAL DATA</h2>
<p>
We process personal data under the following legal justifications, depending on the type of data and how it is used:
</p>
<h3>8. Contractual Necessity</h3>
<p>
We process data that is essential to providing our services, including:
</p>
<ul>
<li>
Account registration, authentication, and profile management and progress saving
</li>
<li>
Enabling multiplayer features, in-game transactions, and digital purchases
</li>
<li>
Providing technical support, responding to inquiries, and troubleshooting issues
</li>
<li>
Delivering game updates, maintenance patches, and security alerts
</li>
<li>User Consent</li>
</ul>
<p>
Without this data, we would be unable to provide essential platform services.
</p>
<h3>9. Legal Compliance & Regulatory Obligations</h3>
<p>
Some data processing is required to comply with legal and regulatory requirements, including:
</p>
<ul>
<li>
Age verification and parental control settings (where required by law)
</li>
<li>
Preventing fraud, financial crimes, and unauthorized access
</li>
<li>
Tax reporting and compliance with financial regulations
</li>
<li>
Responding to law enforcement requests, subpoenas, or court orders
</li>
</ul>
<h3>10. Legitimate Interests</h3>
<p>
We process data to improve security, enhance platform functionality, and optimize user experience, including:
</p>
<ul>
<li>
Anti-cheat monitoring and fraud detection systems
</li>
<li>
Analyzing gameplay trends, matchmaking improvements, and engagement tracking
</li>
<li>
Preventing account abuse, harassment, and disruptive player behavior
</li>
<li>
Ensuring platform stability, performance optimization, and bug reporting
</li>
</ul>
<p>
Users may object to data processing based on legitimate interests unless required by law.
</p>
<h3>11. User Consent</h3>
<p>
In some cases, we collect and process personal data only with your explicit consent, such as:
</p>
<ul>
<li>
Subscribing to marketing communications, newsletters, and promotional emails
</li>
<li>
Enabling personalized advertising, targeted game recommendations, and push notifications
</li>
<li>
Participating in surveys, user experience studies, and beta testing programs
</li>
</ul>
<p>
Users can withdraw consent at any time by contacting DFIANCE via the information provided below.
</p>
<br/>
<h2>DATA RETENTION AND DELETION</h2>
<p>We retain personal data only as long as necessary for legitimate business, security, and compliance reasons.</p>
<h3>12. Standard Retention Periods</h3>
<ul>
<li>
Active Accounts: Personal data is stored for the duration of an active account.
</li>
<li>
Inactive Accounts: If an account remains inactive for 24 months, data may be deleted or anonymized.
</li>
<li>
Financial Transactions: Payment records are retained for legal and tax compliance purposes (e.g., 5-7 years based on jurisdiction).
</li>
</ul>
<h3>13. Data Deletion Requests</h3>
<p>
Users can request deletion of personal data under applicable laws. Deletion requests may be:
</p>
<ul>
<li>
Immediate: If the data is no longer necessary for platform operation.
</li>
<li>
Restricted: If data must be retained for legal compliance or fraud prevention.
</li>
</ul>
<p>
To delete your data, use the in-game ‘Delete Account’ functionality, which will queue your account and associated account data for deletion after a 30 days period. If, for any reason, the in-game 'Delete Account' functionality is not available, you may contact privacy_policy@dfiance.com using your registration email, with the subject "Delete My Data", and your in-game player ID found in your in-game profile settings.  
</p>
<br/>
<h2>DATA SHARING AND TRANSFERS</h2>
<p>
We do not sell personal data. However, certain trusted partners may process data under specific conditions:
</p>
<h3>14. Third-Party Service Providers</h3>
<p>
We share necessary data with:
</p>
<ul>
<li>
Cloud hosting & storage providers for secure infrastructure.
</li>
<li>
Payment processors (e.g., Stripe, PayPal, Apple Pay, et cetera) for in-game purchases.
</li>
<li>
Analytics & advertising networks for improving platform experience (e.g., Google, Facebook, Instagram, et cetera).
</li>
</ul>
<p>
These service providers are contractually bound to protect your data.
</p>
<h3>15. Legal Compliance and Fraud Prevention</h3>
<p>
We may disclose personal data if required by:
</p>
<ul>
<li>
Law enforcement agencies, regulatory bodies, or courts.
</li>
<li>
Fraud detection services & anti-cheat organizations.
</li>
<li>
Government authorities for tax compliance & audits.
</li>
</ul>
<p>
We will only disclose data when legally required or to protect our users and platform from harm.
</p>
<h3>16. International Data Transfers</h3>
<p>
If data is processed outside your country, we ensure appropriate safeguards such as:
</p>
<ul>
<li>
(a)Standard Contractual Clauses (SCCs) for data transfers (EU/EEA users).
</li>
<li>
Data encryption during transmission and storage.
</li>
<li>
Compliance with GDPR, CCPA, and regional data protection laws.
</li>
</ul>
<p>
Users may request additional details about cross-border data transfers via privacy_policy@dfiance.com.      
</p>
<br/>
<h2>USER RESPONSIBILITY</h2>
<p>
While we implement a range of industry-standard measures to protect user data, security is a shared responsibility. We encourage users to take an active role in safeguarding their accounts by:
</p>
<ul>
<li>
Use unique, strong passwords for their accounts.
</li>
<li>
Enable two-factor authentication, when supported (MFA).
</li>
<li>
Beware of phishing scams, fake links, and unauthorized login attempts.
</li>
</ul>
<br/>
<h2>ANTI-CHEAT AND FRAUD PREVENTION</h2>
<p>
We are committed to maintaining a fair and secure gaming environment. To prevent cheating, hacking, and fraud, we use automated detection systems and manual reviews.
</p>
<h3>17. Anti-Cheat Monitoring</h3>
<p>
We employ anti-cheat software that may:
</p>
<ul>
<li>
Analyze gameplay behavior to detect cheating, exploits, or unauthorized modifications.
</li>
<li>
Monitor game files for unauthorized alterations or injected code.
</li>
<li>
Use machine learning and artificial intelligence to identify suspicious player activity and unfair advantages.
</li>
<li>
Record match data to detect any rule violations and/or exploits.
</li>
</ul>
<p>
If cheating is detected, we may:
</p>
<ul>
<li>
Issue temporary or permanent bans from DFIANCE.
</li>
<li>
Restrict access to competitive modes or disable certain account privileges.</li>
<li>
Report cheating behavior to industry-wide anti-cheat organizations.</li>
</ul>
<h3>Fraud Prevention & Payment Security</h3>
<p>
To prevent fraudulent transactions, we may:
</p>
<ul>
<li>
Use AI-driven fraud detection systems to monitor suspicious purchases.
</li>
<li>
Require identity verification for high-value in-game transactions.
</li>
<li>
Flag unusual activity, such as multiple failed payment attempts or account takeovers.
</li>
<li>
Work with payment processors (PayPal, Stripe, Apple Pay, Google Pay, et cetera) to block fraudulent purchases.
</li>
</ul>
<p>
If fraud is detected, we reserve the right to suspend accounts, cancel purchases, or report the case to authorities.
</p>
<br/>
<h2>PARENTAL CONTROLS AND AGE VERIFICATION</h2>
<p>
DFIANCE provides parental controls and safety measures for younger users where legally required.
</p>
<h3>18. Age Restrictions</h3>
<ul>
<li>
If users are under the age of 18, parental consent may be required to create an account.
</li>
<li>
Some regions require additional age verification for compliance with local regulations.
</li>
<li>
If you are a parent or guardian and know that your minor children have provided us with personal data, please follow the instructions under section 13 to remove the data immediately or contact us via the information provided below. If we become aware that we have collected personal data from minor children without verification of parental consent, we will take steps to remove that information from our servers.
</li>
</ul>
<h3>19. Reporting Underage Accounts</h3>
<p>
If a parent or guardian discovers that a child has created an account without consent, they are required to first request the request account deletion using the in-game Delete Account functionality and contact privacy_policy@dfiance.com with the account details (player ID, also found in account settings) so we may prevent account restoration.
</p>
<h2>USER RIGHTS AND DATA REQUESTS</h2>
<p>
Users have the following rights regarding their personal data:
</p>
<h3>20. Right to Access</h3>
<p>
You can request a copy of the personal data we store about you. This includes:
</p>
<ul>
<li>
Account details (email, username, linked services).
</li>
<li>
Purchase history and transaction records.
</li>
<li>
In-game activity logs (if applicable).
</li>
</ul>
<p>
To request access, email privacy_policy@dfiance.com with "Data Access Request" in the subject.
</p>
<h3>21. Right to Correction</h3>
<p>
If personal data is incorrect, incomplete, or outdated, users may update or correct their information via:
</p>
<ul>
<li>
Account settings (for username, email, profile details).
</li>
<li>
Customer support inquiries for security-sensitive changes.
</li>
</ul>
<h3>22. Right to Deletion ("Right to be forgotten")</h3>
<p>
Users may request permanent deletion of their account and personal data unless legally required to retain it.
</p>
<ul>
<li>
Some data (e.g., purchase history, fraud records) cannot be deleted due to legal obligations.
</li>
<li>
Deletion requests can be submitted through the Account Settings and take 30 days to process.
</li>
<li>
Deletion request can be cancelled, within the 30 days processing time frame, through the Account Settings. If submitted, Restore Account requests will cancel the delete request and reset future data deletion requests processing period.
</li>
</ul>
<h3>23. Right to Object to Data Processing</h3>
<p>
Users may object to how we use their data, including:
</p>
<ul>
<li>
Direct marketing communications.
</li>
<li>
Behavioral tracking & targeted advertising (can be adjusted via cookie settings).
</li>
</ul>
<p>
To submit your objection for the above provisions or any other, contact DFIANCE via the contact information provided below.
</p>
<h3>24. Right to Data Portability</h3>
<p>
Users can request their data in a machine-readable format to transfer it to another service. To exercise any of these rights, email privacy_policy@dfiance.com with the specific request in the subject line.
</p>
<br/>
<h2>UPDATES TO THIS PRIVACY POLICY</h2>
<p></p>
<h3>25. Policy Changes</h3>
<p>
We reserve the right to update this Privacy Policy at any time for legal, technical, or business reasons.
</p>
<h3>26. Notification of Changes</h3>
<p>
If significant changes are made, we will notify users via:
</p>
<ul>
<li>
Email alerts (if you have provided an email address).
</li>
<li>
In-platform pop-ups or notifications.
</li>
<li>
Website announcement posts.
</li>
</ul>
<p>
Your continued use of DFIANCE after updates means you accept the revised Privacy Policy. If you disagree with changes, you should stop using our services and delete your account or request data deletion.
</p>
<h2>CONTACT INFORMATION</h2>
<p>For questions or concerns regarding this Privacy Policy, users can reach us via: <b>Email: privacy_policy@dfiance.com</b></p>
				</main>
			</div>
			<div className="land-home land-home--legal-footer noselect">
				<JoinCommunityAndFooter variant="privacy" showSocialSection={false} />
			</div>
		</>
	);
};

export default PrivacyPolicy;